import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Container } from '../components/ui';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import CTASection from '../components/CTASection';
import { BlogCard, formatDate } from '../components/cards';
import NewsletterSignup from '../components/NewsletterSignup';
import { fetchBlogs, resolveImg } from '../lib/marketingApi';

export default function Blog() {
  const { t } = useLanguage();
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    fetchBlogs().then((d) => setPosts(d.items || [])).catch(() => setPosts([]));
  }, []);

  return (
    <div>
      <PageHero eyebrow={t('nav.blog')} title="Insights on utility expense management" subtitle="Practical guides on bill management, payments, sustainability and the data foundation behind them." />
      <section className="px-5 py-16 sm:px-8 sm:py-20">
        <Container>
          {posts === null ? (
            <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : posts.length === 0 ? (
            <div className="py-24 text-center text-slate-400">No articles published yet. Check back soon.</div>
          ) : (
            <>
              {(() => {
                const [featured, ...rest] = posts;
                return (
                  <>
                    <Reveal>
                      <Link to={`/blog/${featured.slug}`} className="group grid grid-cols-1 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:shadow-xl dark:border-white/10 dark:bg-white/[0.03] lg:grid-cols-2">
                        <div className="relative h-60 overflow-hidden lg:h-auto">
                          <img src={resolveImg(featured.image)} alt={featured.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                          <span className="absolute left-4 top-4 rounded-full bg-brand px-3 py-1 text-xs font-semibold text-white">{featured.category}</span>
                        </div>
                        <div className="flex flex-col justify-center p-7 sm:p-10">
                          <div className="text-xs text-slate-400 dark:text-slate-500">{formatDate(featured.date)} · {featured.readTime} min read</div>
                          <h2 className="mt-2 font-display text-2xl font-bold leading-snug text-slate-900 dark:text-white group-hover:text-brand sm:text-3xl">{featured.title}</h2>
                          <p className="mt-3 text-base leading-relaxed text-slate-600 dark:text-slate-300">{featured.excerpt}</p>
                          <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">{t('common.readArticle')} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                        </div>
                      </Link>
                    </Reveal>
                    <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      {rest.map((p, i) => (<Reveal key={p.slug} delay={i * 60}><BlogCard post={{ ...p, image: resolveImg(p.image) }} /></Reveal>))}
                    </div>
                  </>
                );
              })()}
            </>
          )}
        </Container>
      </section>
      <section className="px-5 pb-16 sm:px-8 sm:pb-20">
        <Container>
          <Reveal><div className="mx-auto max-w-3xl"><NewsletterSignup source="blog" /></div></Reveal>
        </Container>
      </section>
      <CTASection />
    </div>
  );
}
